import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../services/API'
import { getWallboardToken, clearWallboardToken } from './wallboardToken'

const POLL_INTERVAL_MS = 60 * 1000

// Polls /wallboard/data every minute with If-None-Match (unchanged data is a
// 304). Modelled on useControlRoomSnapshot. Two auth modes:
//  - 'device': sends the localStorage wallboard token on X-Wallboard-Token;
//    a revoked/invalid token clears storage and fires onTokenRejected so the
//    gate flips back to the unlock screen.
//  - 'session': the normal logged-in cookie; wallboardId can pick a board
//    (used by the editor preview).
// All responses skip the global API error handler — a TV must never be
// redirected or toasted by the app shell.
export function useWallboardData({ mode, wallboardId, onTokenRejected }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [syncState, setSyncState] = useState('connecting')
  const [lastUpdated, setLastUpdated] = useState(null)
  // Subscription/tier notices: { errorCode } — keep polling, auto-recovers
  const [blocked, setBlocked] = useState(null)

  const etagRef = useRef(null)
  const failedPollsRef = useRef(0)
  const onTokenRejectedRef = useRef(onTokenRejected)
  onTokenRejectedRef.current = onTokenRejected

  const fetchData = useCallback(async (options = {}) => {
    try {
      const headers = {}
      // A manual refresh bypasses the ETag — a 304 "nothing changed" looks
      // like a dead button to the person who just pressed it
      if (etagRef.current && options.force !== true)
        headers['If-None-Match'] = etagRef.current
      if (mode === 'device') {
        const token = getWallboardToken()
        if (!token) {
          onTokenRejectedRef.current?.()
          return
        }
        headers['X-Wallboard-Token'] = token
      }

      const res = await API.get('/wallboard/data', {
        headers,
        params: wallboardId ? { wallboardId } : {},
        skipGlobalErrorHandler: true,
      })

      if (res.status === 200 || res.status === 304) {
        failedPollsRef.current = 0
        setSyncState('live')
        setBlocked(null)
        // "Updated" reflects the last successful contact — a 304 confirms
        // the data is still current
        setLastUpdated(new Date())
        if (res.status === 200) {
          etagRef.current = res.headers?.etag ?? null
          setData(res.data)
        }
        return
      }

      if (
        res.status === 401 &&
        ['wallboardTokenRevoked', 'wallboardTokenInvalid'].includes(
          res.data?.errorCode,
        )
      ) {
        clearWallboardToken()
        onTokenRejectedRef.current?.()
        return
      }

      if (res.status === 401 || res.status === 403) {
        // Subscription expired / tier dropped / session ended — show a notice
        // but keep the last data and keep polling so it recovers on its own
        setBlocked({ status: res.status, errorCode: res.data?.errorCode })
        setSyncState('blocked')
        return
      }

      throw new Error(`wallboard poll failed (${res.status})`)
    } catch (err) {
      failedPollsRef.current += 1
      setSyncState(
        !navigator.onLine || failedPollsRef.current >= 3 ? 'offline' : 'delayed',
      )
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [mode, wallboardId])

  useEffect(() => {
    setLoading(true)
    setSyncState('connecting')
    etagRef.current = null
    failedPollsRef.current = 0

    fetchData()
    const interval = setInterval(() => fetchData(), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchData])

  useEffect(() => {
    const online = () => {
      setSyncState('connecting')
      fetchData()
    }
    const offline = () => setSyncState('offline')
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  }, [fetchData])

  const refresh = useCallback(() => fetchData({ force: true }), [fetchData])

  return { data, loading, syncState, lastUpdated, blocked, refresh }
}
